import React, { Suspense, lazy, useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import Landing, { MAP_PATH } from './landing/Landing';
import { LISTING_PATH, OLD_SAVED_LISTINGS_PATH, SAVED_LISTINGS_PATH } from './account/listingRules';
import { PROPERTY_PATH } from './property/propertyValue';
import { PageTracker } from './account/returnPage';
import { BIG_PICTURE_PATH } from './landing/landingScroll';
import RenderLoginGate from './account/RenderLoginGate';
import CookieBanner from './CookieBanner';
import { PageMeta } from './pageMeta';
import { routePattern, track } from './track';
import { AREA_REPORT_PATH } from './areareport/areaReportRules';
import { COOKIES_PATH, FAQ_PATH, PRIVACY_PATH, TERMS_PATH } from './legal/legalPaths';
import { PAYMENT_DONE_PATH, CHANGE_PASSWORD_PATH, CONFIRM_PATH, FORGOT_PATH, LOCATIONS_PATH, LOGIN_PATH, PROFILE_PATH, RESET_PATH, SIGNUP_PATH, SUBSCRIBE_PATH, YOUR_SUBSCRIPTION_PATH, BUY_REPORT_PATH, BUY_PROPERTY_REPORT_PATH } from './account/accountRules';

// Every page but the landing page loads only when opened, so a first visit to
// '/' doesn't download the map app (OpenLayers, charts, every layer)
const MapGate = lazy(() => import('./account/MapGate'));
const ConfirmAccount = lazy(() => import('./account/ConfirmAccount'));
const ForgotPassword = lazy(() => import('./account/ForgotPassword'));
const ResetPassword = lazy(() => import('./account/ResetPassword'));
const Profile = lazy(() => import('./account/Profile'));
// Log in and sign up are one page (account/Login.js), on both paths
const Login = lazy(() => import('./account/Login'));
const ChangePassword = lazy(() => import('./account/ChangePassword'));
const Subscribe = lazy(() => import('./account/Subscribe'));
const BuyReport = lazy(() => import('./account/BuyReport'));
const BuyPropertyReport = lazy(() => import('./account/BuyPropertyReport'));
const YourSubscription = lazy(() => import('./account/YourSubscription'));
const PaymentDone = lazy(() => import('./account/PaymentDone'));
const ListingLookup = lazy(() => import('./account/ListingLookup'));
const SavedListings = lazy(() => import('./account/SavedListings'));
const propertyPage = (name) => lazy(() => import('./property/PropertyLookup').then(m => ({ default: m[name] })));
const PostcodePage = propertyPage('PostcodePage');
const HomesPage = propertyPage('HomesPage');
const PropertyPage = propertyPage('PropertyPage');
const AreaReport = lazy(() => import('./areareport/AreaReport'));
const BigPicture = lazy(() => import('./landing/BigPicture'));
const LegalPage = lazy(() => import('./legal/LegalPage'));
const FaqPage = lazy(() => import('./legal/FaqPage'));

// Share links made before the landing page existed point at '/' with the map's
// state in the query string (see copyShareLink in Layers/BaseLayer.js and the
// params App.js reads). They carry the selected layers in 's', so a '/' link
// with one still opens the map.
export const isMapLink = (search) => new URLSearchParams(search).has('s');

const Home = () => {
    const { search } = useLocation();
    return isMapLink(search) ? <MapGate /> : <Landing />;
};

// A page_view for each page opened, by its route pattern (track.js)
const RouteTracker = () => {
    const { pathname } = useLocation();
    useEffect(() => { track('page_view', routePattern(pathname)); }, [pathname]);
    return null;
};

const AppRoutes = () => (<RenderLoginGate>
    <PageTracker />
    <RouteTracker />
    <PageMeta />
    <CookieBanner />
    <Suspense fallback={null}>
        <Routes>
            <Route path="/" element={<Home />} />
            <Route path={MAP_PATH} element={<MapGate />} />
            <Route path={BIG_PICTURE_PATH} element={<BigPicture />} />
            <Route path={SIGNUP_PATH} element={<Login />} />
            <Route path={CONFIRM_PATH} element={<ConfirmAccount />} />
            <Route path={FORGOT_PATH} element={<ForgotPassword />} />
            <Route path={RESET_PATH} element={<ResetPassword />} />
            <Route path={PROFILE_PATH} element={<Profile />} />
            <Route path={LOGIN_PATH} element={<Login />} />
            <Route path={CHANGE_PASSWORD_PATH} element={<ChangePassword />} />
            {/* Saved locations are now saved areas in My reports */}
            <Route path={LOCATIONS_PATH} element={<Navigate to={SAVED_LISTINGS_PATH + '?tab=area'} replace />} />
            <Route path={SUBSCRIBE_PATH} element={<Subscribe />} />
            <Route path={BUY_REPORT_PATH} element={<BuyReport />} />
            <Route path={BUY_PROPERTY_REPORT_PATH} element={<BuyPropertyReport />} />
            <Route path={YOUR_SUBSCRIPTION_PATH} element={<YourSubscription />} />
            <Route path={PAYMENT_DONE_PATH} element={<PaymentDone />} />
            <Route path={LISTING_PATH} element={<ListingLookup />} />
            <Route path={LISTING_PATH + '/:id'} element={<ListingLookup />} />
            <Route path={SAVED_LISTINGS_PATH} element={<SavedListings />} />
            <Route path={OLD_SAVED_LISTINGS_PATH} element={<Navigate to={SAVED_LISTINGS_PATH} replace />} />
            <Route path={PROPERTY_PATH} element={<PostcodePage />} />
            <Route path={PROPERTY_PATH + '/:postcode'} element={<HomesPage />} />
            <Route path={PROPERTY_PATH + '/:postcode/:uprn'} element={<PropertyPage />} />
            <Route path={AREA_REPORT_PATH} element={<AreaReport />} />
            <Route path={TERMS_PATH} element={<LegalPage page="terms" />} />
            <Route path={PRIVACY_PATH} element={<LegalPage page="privacy" />} />
            <Route path={COOKIES_PATH} element={<LegalPage page="cookies" />} />
            <Route path={FAQ_PATH} element={<FaqPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    </Suspense>
</RenderLoginGate>);

export default AppRoutes;
